import { useAppDispatch, useAppSelector } from 'app/hooks';
import type { EnrichedAccessory } from 'components/layout/modals/compatible-combinations-browser-modal';
import type { SelectedSystemType } from 'data/types';
import { beginCompatibilityAdds, endCompatibilityAdds } from 'features/systems';
import { clearDuctedPairPicks } from 'features/ui';
import { useComparisonSelection } from 'hooks/use-comparison-selection';
import { Dispatch, SetStateAction, useState } from 'react';
import { addDuctedBundleToQuotable } from 'utils/ducted-bundle-add-pipeline';
import type { DuctedBundlePairInput } from 'utils/ducted-bundle-add-types';
import {
  isDuctedBundleReady,
  selectDuctedBundleSlots
} from 'utils/ducted-bundle-picks';
import type { DuctedBundleSlot } from 'utils/ducted-bundle-picks-types';
import { selectActiveDuctedDesign } from 'utils/ducted-design';
import {
  applyAccessoryPicks,
  buildSelectedCombination,
  ComboNeedingSelection,
  findCombosNeedingSelection,
  rebuildCombinationAccessories,
  SelectedCombination
} from 'utils/quotable-add-pipeline';
import { resolveSystemTonnageSeed } from 'utils/system-tonnage-seed';

type PendingBundlePair = Omit<DuctedBundlePairInput, 'combo'> & {
  combo: SelectedCombination;
  askAccessories: boolean;
};

type AccessorySelectionState = {
  openState: [boolean, Dispatch<SetStateAction<boolean>>];
  combinationsWithChoices: ComboNeedingSelection[];
  systemCount: number;
  onConfirm: (
    selections: Map<number, Array<{ accessoryId: number; typeId: number }>>
  ) => void;
};

type UseDuctedBundleAddOptions = {
  onAdded?: (added: SelectedSystemType[]) => void;
  onSettled?: () => void;
};

function mergedAccessoryData(
  slots: DuctedBundleSlot[]
): Record<string, EnrichedAccessory> {
  return Object.assign(
    {},
    ...slots.map((slot) => slot.pick?.selection.accessoryData ?? {})
  );
}

export default function useDuctedBundleAdd({
  onAdded,
  onSettled
}: UseDuctedBundleAddOptions = {}): {
  slots: DuctedBundleSlot[];
  ready: boolean;
  isAdding: boolean;
  beginAdd: () => boolean;
  accessorySelection: AccessorySelectionState;
} {
  const dispatch = useAppDispatch();
  const slots = useAppSelector(selectDuctedBundleSlots);
  const design = useAppSelector(selectActiveDuctedDesign);
  const existingSystems = useAppSelector(
    (state) => state.structure.existingSystems
  );
  const recommendationSelectionsBySystem = useAppSelector(
    (state) => state.structure.recommendationSelectionsBySystem
  );
  const instanceUniqueId = useAppSelector(
    (state) => state.project.instanceUniqueId
  );
  const sizingBands = useAppSelector((state) => state.data.sizingBands);
  const { addSystemsToComparison } = useComparisonSelection();
  const [isAdding, setIsAdding] = useState(false);
  const [accessorySelectionOpen, setAccessorySelectionOpen] = useState(false);
  const [pendingPairs, setPendingPairs] = useState<PendingBundlePair[]>([]);
  const [combosNeedingSelection, setCombosNeedingSelection] = useState<
    ComboNeedingSelection[]
  >([]);

  const ready = isDuctedBundleReady(slots);
  const accessoryData = mergedAccessoryData(slots);
  const handleAdded: (added: SelectedSystemType[]) => void =
    onAdded ?? addSystemsToComparison;

  const toPendingPair = (slot: DuctedBundleSlot): PendingBundlePair | null => {
    if (!slot.pick || !design) return null;
    const { selection, accessoryPicks } = slot.pick;
    const system =
      existingSystems.find(
        (candidate) => candidate.uniqueId === slot.systemUniqueId
      ) ?? null;
    const combo = buildSelectedCombination(
      selection.combination,
      selection.productData,
      selection.keptPairing,
      selection.displayTypeLabel
    );
    return {
      systemUniqueId: slot.systemUniqueId,
      components: system?.components ?? [],
      combo: accessoryPicks
        ? applyAccessoryPicks(
            [combo],
            selection.accessoryData,
            accessoryPicks
          )[0]
        : combo,
      askAccessories: !accessoryPicks,
      tonnageSeed: resolveSystemTonnageSeed({
        system,
        record:
          recommendationSelectionsBySystem[slot.systemUniqueId]?.tonnage ??
          null,
        coolingBtu: slot.context.loads.cooling
      }),
      loads: slot.context.loads,
      designPair: {
        designConfigurationUniqueId: design.uniqueId,
        condenserUniqueId: slot.context.condenserUniqueId,
        condenserName: slot.pairName,
        zoneUniqueId: slot.context.zoneUniqueId
      },
      cardId: selection.cardId
    };
  };

  const runAdd = (pairs: PendingBundlePair[]) => {
    const combinationIds = pairs.map((pair) => pair.combo.combinationId);
    setIsAdding(true);
    dispatch(beginCompatibilityAdds(combinationIds));
    return addDuctedBundleToQuotable({
      pairs,
      instanceUniqueId,
      sizingBands,
      dispatch,
      onSuccess: (added) => {
        handleAdded(added);
        dispatch(clearDuctedPairPicks());
      }
    }).finally(() => {
      dispatch(endCompatibilityAdds(combinationIds));
      setIsAdding(false);
      onSettled?.();
    });
  };

  const beginAdd = (): boolean => {
    if (!ready || isAdding) return false;
    const pairs = slots
      .map(toPendingPair)
      .filter((pair): pair is PendingBundlePair => pair !== null);
    if (pairs.length !== slots.length) return false;

    const needsSelection = findCombosNeedingSelection(
      pairs.filter((pair) => pair.askAccessories).map((pair) => pair.combo),
      accessoryData
    );
    if (needsSelection.length > 0) {
      setPendingPairs(pairs);
      setCombosNeedingSelection(needsSelection);
      setAccessorySelectionOpen(true);
      return true;
    }

    runAdd(
      pairs.map((pair) => ({
        ...pair,
        combo: pair.askAccessories
          ? applyAccessoryPicks([pair.combo], accessoryData, {})[0]
          : pair.combo
      }))
    );
    return true;
  };

  const handleAccessorySelectionConfirm = (
    selections: Map<number, Array<{ accessoryId: number; typeId: number }>>
  ) => {
    const rebuilt = rebuildCombinationAccessories(
      pendingPairs.map((pair) => pair.combo),
      selections,
      accessoryData
    );
    setAccessorySelectionOpen(false);
    runAdd(
      pendingPairs.map((pair, index) => ({ ...pair, combo: rebuilt[index] }))
    );
  };

  return {
    slots,
    ready,
    isAdding,
    beginAdd,
    accessorySelection: {
      openState: [accessorySelectionOpen, setAccessorySelectionOpen],
      combinationsWithChoices: combosNeedingSelection,
      systemCount: pendingPairs.length,
      onConfirm: handleAccessorySelectionConfirm
    }
  };
}
